/**
 * Zudoku API Identity Plugin for Auth0 Client Credentials Flow
 *
 * This plugin enables M2M (Machine-to-Machine) authentication for API requests
 * in the Zudoku playground using OAuth 2.0 Client Credentials grant type.
 */

import { createApiIdentityPlugin } from "zudoku/plugins";

/**
 * Configuration for Auth0 Client Credentials
 */
interface Auth0ClientCredentialsConfig {
  domain: process.env.ZUDOKU_PUBLIC_OKTA_DOMAIN;           // e.g., 'your-tenant.auth0.com'
  clientId: process.env.OAUTH_CLIENT_ID;         // M2M Application Client ID
  clientSecret: process.env.OAUTH_CLIENT_SECRET;     // M2M Application Client Secret
  audience: process.env.ZUDOKU_PUBLIC_JWT_AUDIENCE;         // API Identifier
  scope?: string;           // Optional scopes (space-separated)
}

/**
 * Token cache to avoid requesting new tokens on every API call
 */
interface TokenCache {
  accessToken: string;
  expiresAt: number;
}

/**
 * Creates an Auth0 Client Credentials API Identity Plugin for Zudoku
 *
 * @param config - Auth0 M2M configuration
 * @returns Zudoku API Identity Plugin
 */
export function createAuth0ClientCredentialsPlugin(
  config: Auth0ClientCredentialsConfig
) {
  let tokenCache: TokenCache | null = null;

  /**
   * Request a new access token from Auth0
   */
  async function getAccessToken(): Promise<string> {
    // Check if we have a valid cached token
    if (tokenCache && Date.now() < tokenCache.expiresAt) {
      console.log('[Auth0 M2M] Using cached token');
      return tokenCache.accessToken;
    }

    console.log('[Auth0 M2M] Requesting new token');

    const tokenEndpoint = `https://${config.domain}/oauth/token`;

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        grant_type: 'client_credentials',
        client_id: config.clientId,
        client_secret: config.clientSecret,
        audience: config.audience,
        ...(config.scope && { scope: config.scope }),
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        `Failed to obtain access token: ${error.error_description || error.error}`
      );
    }

    const data = await response.json();
    const { access_token, expires_in } = data;

    // Cache the token with a 5-minute buffer before expiry
    tokenCache = {
      accessToken: access_token,
      expiresAt: Date.now() + (expires_in - 300) * 1000,
    };

    console.log('[Auth0 M2M] Token obtained successfully');
    return access_token;
  }

  /**
   * Create the API Identity Plugin
   */
  return createApiIdentityPlugin({
    getIdentities: async (context) => [
      {
        id: 'auth0-client-credentials',
        label: 'Auth0 M2M Token',
        authorizeRequest: async (request) => {
          try {
            // Get access token
            const token = await getAccessToken();

            // Add Authorization header to the request
            return {
              ...request,
              headers: {
                ...request.headers,
                Authorization: `Bearer ${token}`
              },
            };
          } catch (error) {
            console.error('[Auth0 M2M] Error authorizing request:', error);
            throw error;
          }
        },
      },
    ],
  });
}